import { Printer, Download, Eye } from 'lucide-react';
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ColumnCard, { ColumnProp } from "@/components/ColumnCard";
import { JobSearchSolid } from "@/components/icons";
import IconButton from "@/components/buttons/IconButton";
import Box from "@/components/Box";
import Badge from "@/components/Badge";
import { statusBadgeIntent, statusLabel } from "@/utils/application-status";
import SlideOver from '@/components/SlideOver';
import ShareholderTile from '@/features/applications/pages/fragments/share-holder-tile';
import { useGetLicenseDetails } from '@/features/applications/repositories/licences-repository';
import {useEffect, useState} from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

import 'pdfjs-dist/build/pdf.worker.min.mjs';

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).href;

/** A column is dropped entirely when the licence carries no value for it. */
function column(heading: string, content?: string | number | null): ColumnProp | null {
    if (content === undefined || content === null || content === "") return null;
    return { heading, content };
}

function presentColumns(...columns: Array<ColumnProp | null>): ColumnProp[] {
    return columns.filter((el): el is ColumnProp => el !== null);
}

/** Shape of `LicenseRequestDTO.ShareholderRequest`, which names the role `shareholderPosition`. */
type ShareholderRequest = {
    id?: string;
    firstName?: string;
    lastName?: string;
    shareholderPosition?: string;
};

const EmptySection = ({ children }: { children: string }) => (
    <p className="text-sm text-[#6B5C4F]">{children}</p>
);

/**
 * Detail view for an issued licence, reading `LicenseRequestDTO`
 * (GET /licence/{id}). Everything shown is a field the mapper actually
 * populates — `LicenseMapper.toLicenseRequest` — and any field this particular
 * licence has no value for is omitted rather than printed as a placeholder.
 *
 * The record is fetched by the route's id. It used to be read from
 * `location.state` alone, so refreshing the page or opening the link directly
 * left every field empty; the navigation state is now only a first paint while
 * the request is in flight.
 */
const LincenceDetails = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { applicationId } = useParams();
    const { licence_details, isLoading } = useGetLicenseDetails(applicationId!);

    const fetched = licence_details?.results ?? licence_details;
    const licences = fetched ?? location.state?.licences;

    // State for preview and download
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string>('');
    const [isFileLoading, setIsFileLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [blobCache, setBlobCache] = useState<Map<string, string>>(new Map());

    // The official licence number is issued on approval; `id` is only the row's
    // primary key and must never be dressed up as a licence number.
    const licenceNumber = licences?.licenseNumber ?? licences?.applicationId;
    const fileName = `license-${licenceNumber ?? licences?.id ?? 'document'}.pdf`;

    const columns = presentColumns(
        column("License Number", licences?.licenseNumber),
        column("Application ID", licences?.applicationId),
        column("Date Issued", licences?.issuedDate),
        column("Expiry Date", licences?.expiryDate),
        column("Previous License", licences?.previousLicenseNumber),
    );

    const businessColumns = presentColumns(
        column("Physical Location", licences?.physicalLocation),
        column("Region", licences?.region),
        column("District", licences?.district),
    );

    const services: Array<{ id?: number; label?: string }> = licences?.selectedServices ?? [];
    const shareholders: ShareholderRequest[] = licences?.businessShareHolders ?? [];
    const tourCompanies: Array<{ companyName?: string; tourCompanyTransfer?: string }> =
        licences?.tourCompanies ?? [];

    const close = () => navigate(-1);


    // Clean up blob URLs when the component unmounts
    const cleanupBlob = (url: string) => {
        if (blobCache.has(url)) {
            URL.revokeObjectURL(blobCache.get(url)!);
            setBlobCache(prev => {
                const newCache = new Map(prev);
                newCache.delete(url);
                return newCache;
            });
        }
    };

    useEffect(() => {
        return () => {
            blobCache.forEach((_, url) => cleanupBlob(url));
        };
    }, []);

    // Function to fetch the PDF and create a local blob URL
    const fetchLicenseFile = async (url: string) => {
        setIsFileLoading(true);
        setError(null);

        try {
            // Check if the file is already in the cache
            if (blobCache.has(url)) {
                setPreviewUrl(blobCache.get(url)!);
                setIsFileLoading(false);
                return blobCache.get(url)!;
            }

            // Fetch the file
            const response = await fetch(url, { method: 'GET', credentials: 'omit' });

            if (!response.ok) throw new Error(`Failed to download file: ${response.statusText}`);

            const blob = await response.blob();

            if (blob.size === 0) throw new Error('File appears to be empty');

            // Verify that the file is a PDF
            const text = await blob.slice(0, 4).text();
            if (text !== '%PDF') {
                throw new Error('File is not a valid PDF');
            }

            const localUrl = URL.createObjectURL(blob);

            // Cache the local URL
            setBlobCache(prev => new Map(prev).set(url, localUrl));
            setPreviewUrl(localUrl);
            return localUrl;
        } catch (err: any) {
            console.error('[fetchLicenseFile] Error:', err);
            setError(`Failed to load file: ${err.message}`);
            throw err;
        } finally {
            setIsFileLoading(false);
        }
    };

    // Function to handle PDF download
    const handleDownload = async () => {
        if (!licences?.licenseUrl) {
            alert("License document is not available for download");
            return;
        }

        try {
            const localUrl = await fetchLicenseFile(licences.licenseUrl);
            const link = document.createElement('a');
            link.href = localUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err: any) {
            console.error('[handleDownload] Download error:', err);
            alert(`Failed to download file: ${err.message}`);
        }
    };

    // Function to toggle PDF preview
    const togglePreview = async () => {
        if (!licences?.licenseUrl) {
            alert("License document is not available for preview");
            return;
        }

        try {
            if (isPreviewOpen) {
                // Close the preview
                setIsPreviewOpen(false);
                cleanupBlob(licences.licenseUrl);
                setPreviewUrl('');
                setError(null);
            } else {
                // Open the preview
                await fetchLicenseFile(licences.licenseUrl);
                setIsPreviewOpen(true);
            }
        } catch (err: any) {
            console.error('[togglePreview] Preview error:', err);
            alert(`Failed to load preview: ${err.message}`);
        }
    };

    // Render the preview content
    const renderPreviewContent = () => {
        if (!isPreviewOpen) return null;

        if (isFileLoading) {
            return (
                <div className="flex justify-center items-center h-full">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
            );
        }

        if (error) {
            return (
                <div className="text-center p-4">
                    <p className="text-red-500">{error}</p>
                    <button
                        onClick={handleDownload}
                        className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                    >
                        Download Instead
                    </button>
                </div>
            );
        }

        return (
            <div className="w-full h-full overflow-auto">
                <Document
                    file={previewUrl}
                    onLoadError={(e) => {
                        console.error('[renderPreviewContent] PDF load error with react-pdf:', e);
                        setError('Failed to load PDF preview');
                    }}
                >
                    <Page pageNumber={1} />
                </Document>
            </div>
        );
    };

    const status = statusLabel(licences?.applicationStatus);
    const projectName = licences?.projectName ?? licences?.businessName;

    return (
        <SlideOver
            title={licenceNumber ? `License Preview ${licenceNumber}` : "License Preview"}
            icon={(
                <IconButton
                    intent="primary"
                    className="border border-zinc-200 shadow-sm p-2.5 rounded-lg"
                >
                    <JobSearchSolid className="size-6" />
                </IconButton>
            )}
            size="md"
            onClose={close}
            isOpen={true}
        >
            {isLoading && !licences ? (
                <div className="flex justify-center items-center py-20">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
                </div>
            ) : (
            <div className="space-y-4 md:space-y-8">
                <Box className="">
                    <div className="flex gap-4">
                        <div className='inline-flex space-x-2 items-center cursor-pointer' onClick={handleDownload}>
                            <Download className="size-4 text-center" />
                            <span className='text-sm text-center'>Download</span>
                        </div>

                        <div
                            className='inline-flex space-x-2 items-center cursor-pointer'
                            onClick={() => window.print()}
                        >
                            <Printer className="size-5" />
                            <span className='text-sm text-center'>Print</span>
                        </div>
                    </div>
                    <div className='flex justify-between mt-5'>
                        <div className='text-xl font-semibold'>
                            {projectName}
                        </div>
                        <IconButton intent="default" onClick={togglePreview}>
                            <Eye className="size-5" />
                        </IconButton>
                    </div>

                    {/* PDF Preview */}
                    {isPreviewOpen && (
                        <div className="mt-4">
                            <h6 className="font-semibold mb-2">License Preview</h6>
                            <div className="border rounded-lg overflow-hidden h-[80vh]">
                                {renderPreviewContent()}
                            </div>
                        </div>
                    )}
                </Box>

                <Box className="space-y-4">
                    <div className="flex justify-between items-start gap-4">
                        <div className="space-y-1">
                            {projectName && (
                                <h5 className="text-secondary font-semibold">
                                    {projectName}
                                </h5>
                            )}
                            {status && (
                                <p className="text-sm font-light">
                                    Application Status: {status}
                                </p>
                            )}
                        </div>
                        {status && (
                            <Badge
                                intent={statusBadgeIntent(licences?.applicationStatus)}
                                text={status}
                            />
                        )}
                    </div>
                    {columns.length > 0 ? (
                        <ColumnCard columns={columns} className="grid-cols-3" />
                    ) : (
                        <EmptySection>This licence has not been issued yet, so it carries no licence number or dates.</EmptySection>
                    )}
                </Box>

                <Box className="space-y-4">
                    <h6 className="font-semibold">Business Details</h6>
                    {businessColumns.length > 0 ? (
                        <ColumnCard columns={businessColumns} className="grid-cols-3" />
                    ) : (
                        <EmptySection>No business address recorded for this licence.</EmptySection>
                    )}
                </Box>

                <Box className="space-y-4">
                    <h6 className="font-semibold">Services</h6>
                    {services.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                            {services.map((service) => (
                                <Badge
                                    key={service.id}
                                    intent="default"
                                    text={service.label ?? ""}
                                />
                            ))}
                        </div>
                    ) : (
                        <EmptySection>No services recorded for this licence.</EmptySection>
                    )}
                </Box>

                <Box className="space-y-4">
                    <h6 className="font-semibold">Share Holders</h6>
                    {shareholders.length > 0 ? (
                        <div className="space-y-2">
                            <ShareholderTile
                                shareholders={shareholders.map((shareholder, index) => ({
                                    id: Number(shareholder.id) || index,
                                    firstName: shareholder.firstName ?? "",
                                    lastName: shareholder.lastName ?? "",
                                    // The DTO names the role `shareholderPosition`; there is no `position`.
                                    position: shareholder.shareholderPosition ?? "",
                                }))}
                            />
                        </div>
                    ) : (
                        <EmptySection>No shareholders recorded for this licence.</EmptySection>
                    )}
                </Box>

                <Box className="space-y-4">
                    <h6 className="font-semibold">Tour Companies</h6>
                    {tourCompanies.length > 0 ? (
                        <div className="space-y-2">
                            {tourCompanies.map((company, index) => (
                                <div key={index} className="p-3 bg-gray-50 rounded-lg">
                                    <p className="font-medium">{company.companyName}</p>
                                    <p className="text-sm text-gray-600">{company.tourCompanyTransfer}</p>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <EmptySection>No tour companies recorded for this licence.</EmptySection>
                    )}
                </Box>
            </div>
            )}
        </SlideOver>
    );
};

export default LincenceDetails;
